import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Store, Plus, Pencil, Trash2, Image as ImageIcon, X, Camera, QrCode, Printer, Wallet } from "lucide-react";
import { resolveImageUrl } from "../context/AppSettingsContext";
import { BranchReportRow, BranchReportsToolbar } from "../components/BranchReports";
import { BranchGallery } from "../components/BranchGallery";
import ProximitySettingsCard from "../components/ProximitySettingsCard";
import FlashOfferCard from "../components/FlashOfferCard";
import FlashRankingCard from "../components/FlashRankingCard";

const APP_ORIGIN = window.location.origin;

export default function Branches() {
  const [rows, setRows] = useState([]);
  const [reportStatus, setReportStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingField, setUploadingField] = useState(null); // "card_bg_url" | "logo_url" | "store_image_url"
  const [qrBranch, setQrBranch] = useState(null);
  const savePdf = (data, name) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); };
  const downloadPoster = async (b) => {
    try { const { data } = await api.get(`/branches/${b.id}/qr-poster.pdf`, { responseType: "blob" }); savePdf(data, `locandina_qr_${(b.name || "sede").replace(/\s+/g, "_")}.pdf`); toast.success(`Locandina QR di ${b.name} scaricata`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadPosters = async () => {
    try { const { data } = await api.get("/branches/qr-poster.pdf", { responseType: "blob" }); savePdf(data, "locandine_qr_sedi.pdf"); toast.success("Locandine QR di tutte le sedi scaricate"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadWalletPoster = async (b) => {
    try {
      const { data } = await api.get(b ? `/branches/${b.id}/wallet-poster.pdf` : "/branches/wallet-poster.pdf", { responseType: "blob" });
      savePdf(data, b ? `locandina_wallet_${(b.name || "sede").replace(/\s+/g, "_")}.pdf` : "locandine_wallet_sedi.pdf");
      toast.success(b ? `Locandina Wallet di ${b.name} scaricata` : "Locandine Wallet di tutte le sedi scaricate");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const empty = { name: "", address: "", phone: "", card_bg_url: "", logo_url: "", store_image_url: "", receipt_footer: "", manager_name: "", manager_phone: "", proximity_text: "", active: true };
  const [form, setForm] = useState(empty);
  const fileInputs = useRef({});

  const [posterStats, setPosterStats] = useState({});
  const load = async () => {
    setRows((await api.get("/branches")).data);
    api.get("/branches/weekly-report/status").then((r) => setReportStatus(r.data)).catch(() => setReportStatus(null));
    api.get("/branches/poster-stats").then((r) => setPosterStats(r.data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (editing) await api.patch(`/branches/${editing.id}`, form);
      else await api.post("/branches", form);
      setOpen(false); toast.success("Sede salvata"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare la sede?")) return;
    await api.delete(`/branches/${id}`); toast.success("Eliminata"); load();
  };

  const uploadBranchImage = async (branch, field, file, successMsg) => {
    if (!file) return;
    if (!file.type.includes("image")) {
      toast.error("Carica un file immagine");
      return;
    }
    setUploadingId(branch.id);
    setUploadingField(field);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.patch(`/branches/${branch.id}`, { [field]: data.url });
      toast.success(successMsg);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingId(null); setUploadingField(null); }
  };

  const clearBranchImage = async (branch, field, confirmMsg, successMsg) => {
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/branches/${branch.id}`, { [field]: "" });
      toast.success(successMsg);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="branches-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><Store className="h-3 w-3" /> SEDI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Punti vendita</h1>
          <p className="text-zinc-400 mt-1">Ogni sede può avere una grafica carta dedicata; il logo del circuito resta comune.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="branches-qr-posters" onClick={downloadPosters} className="border-zinc-800 h-11" title="PDF con locandina A4 + 4 cartellini per ogni sede (QR di registrazione con sede preselezionata)">
            <QrCode className="h-4 w-4 mr-2" /> Locandine QR (tutte le sedi)
          </Button>
          <Button variant="outline" data-testid="branches-wallet-posters" onClick={() => downloadWalletPoster(null)} className="border-zinc-800 h-11" title="Locandina A4 + 4 cartellini «La tua card sul telefono» (QR → /wallet) per ogni sede">
            <Wallet className="h-4 w-4 mr-2" /> Locandine Wallet
          </Button>
          <Button data-testid="branch-new" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
            <Plus className="h-4 w-4 mr-2" /> Nuova sede
          </Button>
        </div>
      </div>

      {reportStatus && <BranchReportsToolbar status={reportStatus} onDone={load} />}
      <ProximitySettingsCard />
      <FlashOfferCard branches={rows} />
      <FlashRankingCard />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <div className="text-zinc-500 col-span-full">Nessuna sede.</div>}
        {rows.map((b) => {
          const bgUrl = resolveImageUrl(b.card_bg_url);
          const logoUrl = resolveImageUrl(b.logo_url);
          const storeUrl = resolveImageUrl(b.store_image_url);
          const isUp = (f) => uploadingId === b.id && uploadingField === f;
          return (
            <div key={b.id} className="card-elev p-5 flex flex-col gap-3" data-testid={`branch-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-12 w-12 rounded-xl bg-red-500/10 text-red-300 flex items-center justify-center shrink-0 overflow-hidden">
                    {logoUrl ? (
                      <img src={logoUrl} alt="logo" className="h-full w-full object-cover" data-testid={`branch-logo-preview-${b.id}`} />
                    ) : (
                      <Store className="h-5 w-5" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-lg truncate">{b.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{b.address || "—"}</div>
                  </div>
                </div>
                <span className={`pill ${b.active ? "pill-mint" : "pill-muted"}`}>{b.active ? "Attiva" : "Chiusa"}</span>
              </div>
              <div className="text-sm text-zinc-400">{b.phone}</div>
              <div className="flex items-center gap-2 text-xs" data-testid={`branch-poster-stats-${b.id}`} title="Clienti registrati inquadrando il QR della locandina di questa sede">
                <Printer className="h-3.5 w-3.5 text-[color:var(--paprika)]" />
                <span className="font-semibold">{posterStats[b.id]?.poster || 0}</span>
                <span className="text-zinc-500">registrati da locandina QR · {posterStats[b.id]?.total || 0} iscritti alla sede</span>
              </div>
              {reportStatus?.crm_ready && <BranchReportRow branch={b} crmReady onDone={load} />}

              {/* Store photo (Punto Vendita) */}
              <div
                className="relative rounded-xl overflow-hidden border border-zinc-800 aspect-[16/9] bg-zinc-950"
                data-testid={`branch-store-preview-${b.id}`}
                style={storeUrl ? {
                  backgroundImage: `url(${storeUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                } : {}}
              >
                {!storeUrl && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-600 text-xs gap-1">
                    <Camera className="h-8 w-8 opacity-30" />
                    <span>Foto punto vendita</span>
                  </div>
                )}
                <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-black/60 rounded px-2 py-1 text-white">
                  Punto vendita
                </div>
              </div>

              {/* Card graphic preview */}
              <div
                className="relative rounded-xl overflow-hidden border border-zinc-800 aspect-[1.586/1] bg-zinc-950"
                data-testid={`branch-card-preview-${b.id}`}
                style={bgUrl ? {
                  backgroundImage: `url(${bgUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                } : {}}
              >
                {!bgUrl && (
                  <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-xs">
                    <ImageIcon className="h-8 w-8 opacity-30" />
                  </div>
                )}
                <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-black/60 rounded px-2 py-1 text-white">
                  {bgUrl ? "Grafica sede" : "Grafica globale"}
                </div>
              </div>

              {/* Hidden file inputs per field */}
              <input
                ref={(el) => (fileInputs.current[`${b.id}:card_bg_url`] = el)}
                type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                data-testid={`branch-card-file-${b.id}`}
                onChange={(e) => uploadBranchImage(b, "card_bg_url", e.target.files?.[0], "Grafica carta aggiornata")}
              />
              <input
                ref={(el) => (fileInputs.current[`${b.id}:logo_url`] = el)}
                type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                data-testid={`branch-logo-file-${b.id}`}
                onChange={(e) => uploadBranchImage(b, "logo_url", e.target.files?.[0], "Logo sede aggiornato")}
              />
              <input
                ref={(el) => (fileInputs.current[`${b.id}:store_image_url`] = el)}
                type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                data-testid={`branch-store-file-${b.id}`}
                onChange={(e) => uploadBranchImage(b, "store_image_url", e.target.files?.[0], "Foto punto vendita aggiornata")}
              />

              <div className="grid grid-cols-3 gap-2">
                <Button
                  data-testid={`branch-logo-upload-${b.id}`}
                  size="sm" variant="outline"
                  disabled={isUp("logo_url")}
                  onClick={() => fileInputs.current[`${b.id}:logo_url`]?.click()}
                  className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                >
                  <ImageIcon className="h-3.5 w-3.5 mr-1" /> {isUp("logo_url") ? "…" : "Logo"}
                </Button>
                <Button
                  data-testid={`branch-store-upload-${b.id}`}
                  size="sm" variant="outline"
                  disabled={isUp("store_image_url")}
                  onClick={() => fileInputs.current[`${b.id}:store_image_url`]?.click()}
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                >
                  <Camera className="h-3.5 w-3.5 mr-1" /> {isUp("store_image_url") ? "…" : "Foto PV"}
                </Button>
                <Button
                  data-testid={`branch-card-upload-${b.id}`}
                  size="sm" variant="outline"
                  disabled={isUp("card_bg_url")}
                  onClick={() => fileInputs.current[`${b.id}:card_bg_url`]?.click()}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                >
                  <ImageIcon className="h-3.5 w-3.5 mr-1" /> {isUp("card_bg_url") ? "…" : "Carta"}
                </Button>
              </div>

              <BranchGallery branch={b} onChanged={load} />

              {(logoUrl || storeUrl || bgUrl) && (
                <div className="flex flex-wrap gap-2">
                  {logoUrl && (
                    <Button data-testid={`branch-logo-clear-${b.id}`} size="sm" variant="outline"
                      onClick={() => clearBranchImage(b, "logo_url", `Rimuovere il logo di ${b.name}?`, "Logo rimosso")}
                      className="border-zinc-800 hover:border-red-500 hover:text-red-300 text-xs">
                      <X className="h-3 w-3 mr-1" /> Logo
                    </Button>
                  )}
                  {storeUrl && (
                    <Button data-testid={`branch-store-clear-${b.id}`} size="sm" variant="outline"
                      onClick={() => clearBranchImage(b, "store_image_url", `Rimuovere la foto punto vendita di ${b.name}?`, "Foto rimossa")}
                      className="border-zinc-800 hover:border-red-500 hover:text-red-300 text-xs">
                      <X className="h-3 w-3 mr-1" /> Foto PV
                    </Button>
                  )}
                  {bgUrl && (
                    <Button data-testid={`branch-card-clear-${b.id}`} size="sm" variant="outline"
                      onClick={() => clearBranchImage(b, "card_bg_url", `Rimuovere la grafica personalizzata per ${b.name}?`, "Grafica rimossa (verrà usato il tema globale)")}
                      className="border-zinc-800 hover:border-red-500 hover:text-red-300 text-xs">
                      <X className="h-3 w-3 mr-1" /> Carta
                    </Button>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadPoster(b)}
                  data-testid={`branch-qr-poster-${b.id}`} title="Locandina QR fedeltà (A4 + 4 cartellini da banco) per questa sede"
                  className="border-[color:var(--paprika)]/40 text-[color:var(--paprika)] hover:bg-[color:var(--paprika)]/10 flex-1">
                  <Printer className="h-3.5 w-3.5 mr-1" /> Locandina
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadWalletPoster(b)}
                  data-testid={`branch-wallet-poster-${b.id}`} title="Locandina «La tua card sul telefono»: QR verso /wallet per recuperare la card senza app"
                  className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 flex-1">
                  <Wallet className="h-3.5 w-3.5 mr-1" /> Wallet
                </Button>
                <Button size="sm" variant="outline" onClick={() => setQrBranch(b)}
                  data-testid={`branch-qr-menu-${b.id}`}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex-1">
                  <QrCode className="h-3.5 w-3.5 mr-1" /> QR Prodotti
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(b); setForm({ ...empty, ...b }); setOpen(true); }} data-testid={`branch-edit-${b.id}`} className="border-zinc-800 flex-1">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(b.id)} className="border-zinc-800 hover:border-red-500 hover:text-red-300">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800">
          <DialogHeader><DialogTitle>{editing ? "Modifica sede" : "Nuova sede"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300 mb-1 inline-block">Nome</Label>
              <Input data-testid="branch-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Indirizzo</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Messaggio di prossimità (Wallet + web app)</Label>
              <Input data-testid="branch-form-proximity-text" maxLength={200} value={form.proximity_text || ""} onChange={(e) => setForm({ ...form, proximity_text: e.target.value })} className="bg-zinc-950 border-zinc-800" placeholder="Es. Oggi punti doppi da {branch}! Vuoto = testo predefinito" />
            </div>
            <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div className="col-span-2 text-xs text-zinc-400">Responsabile punto vendita (riceve il report AI settimanale su WhatsApp)</div>
              <div><Label className="text-zinc-300 mb-1 inline-block text-xs">Nome</Label>
                <Input data-testid="branch-form-manager-name" value={form.manager_name || ""} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} className="bg-zinc-900 border-zinc-800" placeholder="Mario Rossi" />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block text-xs">WhatsApp</Label>
                <Input data-testid="branch-form-manager-phone" value={form.manager_phone || ""} onChange={(e) => setForm({ ...form, manager_phone: e.target.value })} className="bg-zinc-900 border-zinc-800" placeholder="+39 333 1234567" />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div className="text-sm font-medium">Attiva</div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div className="text-[11px] text-zinc-500">Logo, foto Punto Vendita e grafica carta si caricano dai bottoni dedicati sulla card della sede.</div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Piè di pagina scontrino (opzionale)</Label>
              <textarea
                data-testid="branch-form-receipt-footer"
                value={form.receipt_footer || ""}
                onChange={(e) => setForm({ ...form, receipt_footer: e.target.value })}
                rows={3}
                placeholder="Es. Grazie di aver scelto la nostra sede — Aperti dal martedì alla domenica"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md p-2 text-sm resize-y"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Se valorizzato, sovrascrive il piè di pagina globale per gli scontrini stampati da questa sede.
              </p>
            </div>
            <Button data-testid="branch-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {editing ? "Salva" : "Crea sede"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Menù dialog: shows a printable QR that links to the public branch menu */}
      <Dialog open={!!qrBranch} onOpenChange={(o) => !o && setQrBranch(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md" data-testid="branch-qr-dialog">
          {qrBranch && (() => {
            const menuUrl = `${APP_ORIGIN}/menu-sede/${qrBranch.id}`;
            const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=8&bgcolor=ffffff&color=18181c&data=${encodeURIComponent(menuUrl)}`;
            const doPrint = () => {
              const html = `<!doctype html><html><head><title>QR Prodotti ${qrBranch.name}</title>
                <style>
                  body{font-family:system-ui,sans-serif;padding:32px;text-align:center;color:#111}
                  h1{margin:0 0 8px;font-size:22px}
                  .sub{color:#666;font-size:13px;margin-bottom:24px}
                  img{width:360px;height:360px}
                  .cta{margin-top:18px;font-size:18px;font-weight:600}
                  .url{margin-top:6px;font-size:11px;color:#666;word-break:break-all}
                </style></head><body>
                <h1>${qrBranch.name}</h1>
                <div class="sub">${qrBranch.address || ""}</div>
                <img src="${qrImg}" alt="QR menu" />
                <div class="cta">Scansiona per vedere il menù</div>
                <div class="url">${menuUrl}</div>
                <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
                </body></html>`;
              const w = window.open("", "_blank", "width=600,height=800");
              if (!w) { toast.error("Popup bloccato dal browser"); return; }
              w.document.open(); w.document.write(html); w.document.close();
            };
            return (
              <>
                <DialogHeader><DialogTitle>QR Prodotti · {qrBranch.name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-[11px] text-zinc-500">
                    Stampa questo QR e affiggilo in sede. I clienti (anche senza registrazione)
                    lo scansionano per aprire il menù della sede.
                  </p>
                  <div className="rounded-xl bg-white p-4 flex items-center justify-center">
                    <img src={qrImg} alt="QR menù sede" data-testid="branch-qr-image" className="w-56 h-56" />
                  </div>
                  <div className="text-[11px] text-center text-zinc-500 font-mono break-all">{menuUrl}</div>
                  <div className="flex gap-2">
                    <Button data-testid="branch-qr-print" onClick={doPrint} className="flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                      <Printer className="h-4 w-4 mr-2" /> Stampa QR
                    </Button>
                    <Button variant="outline" data-testid="branch-qr-copy"
                      onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success("Link copiato"); }}
                      className="border-zinc-800">Copia link</Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
